import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  AlertTriangle, 
  Package, 
  Clock, 
  TrendingDown,
  ChevronDown,
  ChevronUp,
  X
} from "lucide-react";
import axios from "axios";
import { BACKEND_URL as __BACKEND_URL__ } from "../../lib/backend-url";

const API = `${__BACKEND_URL__}/api`;

export const AlertsSection = ({ 
  selectedStoreIds = [],
  onProductClick,
}) => {
  const [expiringProducts, setExpiringProducts] = useState([]);
  const [slowSellingProducts, setSlowSellingProducts] = useState([]);
  const [showAllExpiring, setShowAllExpiring] = useState(false);
  const [showAllSlow, setShowAllSlow] = useState(false);

  useEffect(() => {
    loadAlerts();
  }, [selectedStoreIds]);

  const loadAlerts = async () => {
    try {
      // Load expiring products (exclude already expired)
      const expiringParams = new URLSearchParams();
      if (selectedStoreIds.length === 1) {
        expiringParams.append("store_id", selectedStoreIds[0]);
      }
      expiringParams.append("days", "5");
      expiringParams.append("min_count", "10");
      expiringParams.append("exclude_expired", "true");
      
      const expiringResponse = await axios.get(`${API}/stock/expiring?${expiringParams.toString()}`);
      setExpiringProducts(expiringResponse.data.products || []);
      
      // Load slow selling products
      const slowParams = new URLSearchParams();
      if (selectedStoreIds.length === 1) {
        slowParams.append("store_id", selectedStoreIds[0]);
      }
      const slowResponse = await axios.get(`${API}/stock/slow-selling?${slowParams.toString()}`);
      setSlowSellingProducts(slowResponse.data.products || []);
    } catch (error) {
      console.error("Error loading alerts:", error);
    }
  };

  const displayedExpiring = showAllExpiring ? expiringProducts : expiringProducts.slice(0, 6);
  const displayedSlow = showAllSlow ? slowSellingProducts : slowSellingProducts.slice(0, 6);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Expiring Products Alert */}
      {expiringProducts.length > 0 && (
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800">
              <Clock className="w-5 h-5" />
              Produtos com Vencimento Próximo
              <span className="ml-auto text-sm font-normal text-amber-600">
                {expiringProducts.length} produtos
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {displayedExpiring.map((product, index) => (
                <div
                  key={product.ean || index}
                  onClick={() => onProductClick?.(product.name)}
                  className={`
                    flex items-center justify-between p-2 rounded-lg cursor-pointer
                    transition-colors hover:bg-amber-100
                    ${product.days_until_expiry <= 0 
                      ? 'bg-red-100 border border-red-300' 
                      : product.days_until_expiry <= 2 
                        ? 'bg-amber-100 border border-amber-300'
                        : 'bg-white border border-amber-200'
                    }
                  `}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {product.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      EAN: {product.ean} | Estoque: {product.stock?.toFixed(0) || 0}
                    </p>
                  </div>
                  <div className="text-right ml-2">
                    <p className={`text-sm font-bold ${
                      product.days_until_expiry <= 0 
                        ? 'text-red-600' 
                        : product.days_until_expiry <= 2 
                          ? 'text-amber-600'
                          : 'text-orange-600'
                    }`}>
                      {product.days_until_expiry <= 0 
                        ? 'Vence hoje!' 
                        : `${product.days_until_expiry} dias`
                      }
                    </p>
                    <p className="text-[10px] text-slate-400">
                      {product.expiry_date}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {expiringProducts.length > 6 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllExpiring(!showAllExpiring)}
                className="w-full mt-3 text-amber-700 hover:text-amber-800 hover:bg-amber-100"
              >
                {showAllExpiring ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-1" />
                    Mostrar menos
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Ver mais {expiringProducts.length - 6} produtos
                  </>
                )}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Slow Selling Products Alert */}
      {slowSellingProducts.length > 0 && (
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-blue-800">
              <TrendingDown className="w-5 h-5" />
              Produtos com Baixa Rotatividade
              <span className="ml-auto text-sm font-normal text-blue-600">
                {slowSellingProducts.length} produtos
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {displayedSlow.map((product, index) => (
                <div
                  key={product.ean || index}
                  onClick={() => onProductClick?.(product.name)}
                  className="flex items-center justify-between p-2 rounded-lg bg-white border border-blue-200 cursor-pointer hover:bg-blue-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {product.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      EAN: {product.ean} | Estoque: {product.stock?.toFixed(0) || 0}
                    </p>
                  </div>
                  <div className="text-right ml-2">
                    <p className="text-sm font-bold text-blue-600">
                      {product.days_without_sale || 0} dias
                    </p>
                    <p className="text-[10px] text-slate-400">sem venda</p>
                  </div>
                </div>
              ))}
            </div>
            
            {slowSellingProducts.length > 6 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllSlow(!showAllSlow)}
                className="w-full mt-3 text-blue-700 hover:text-blue-800 hover:bg-blue-100"
              >
                {showAllSlow ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-1" />
                    Mostrar menos
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Ver mais {slowSellingProducts.length - 6} produtos
                  </>
                )}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AlertsSection;
